import { Button } from "../../../components/Button"
import { useEffect, useState } from "react"
import EnquiryForm from "../../../components/EnquiryForm";
import { SubjectDTO, HighlightItem, Trimester, DurationOption, FlexibilityPoint, TrimesterItem, HeroData } from "../../../data/subjects";
import { useNavigate, useParams } from "react-router-dom";
import xhr from "../../../utils/fetch";
import { toast } from "react-toastify";
import Footer from "../../../components/Landing/Footer";
import { SEO } from "../../../components/SEO";

// // Showcase component to render multiple subjects from a single array
// export const SubjectsShowcase = ({ subjects }: { subjects?: SubjectDTO[] }) => {

//   const data = subjects ?? subjectsData

//   return (
//     <div className="space-y-16">
//       {data.map((subj) => (
//         <div key={subj.id}>
//           <SubjectDetails subject={subj} />
//         </div>
//       ))}
//     </div>
//   )
// }

export type CurriculumItem = TrimesterItem

const CheckmarkIcon = () => (
  <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
  </svg>
);

const HeroSection = ({ heroData , heroImageUrl }: { heroData: HeroData , heroImageUrl:string }) => {
  const navigate = useNavigate();
  return (
    <div className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="container mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        {/* Left Column */}
        <div className="text-left">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-800 leading-tight">
            {heroData.title}
          </h1>
          <p className="mt-6 text-lg text-gray-600">
            {heroData.description}
          </p>
          <div className="mt-8 flex flex-wrap gap-4 text-white font-bold text-lg">
            <Button text={`Book a Free Trial`} onClick={() => {navigate('/booktrail')}} bgColor="bg-secondary"  />
            <button className="px-6 py-2 text-sm font-semibold border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50" onClick={()=>{navigate('/#findTutor')}} >
            Learn More About the Curriculum
            </button>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-4">
            {heroData.stats.map((stat, index) => (
              <div key={index} className="flex items-center text-gray-600">
                <CheckmarkIcon />
                <span>{stat}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right Column */}
        <div className="relative">
          <div className="bg-white/40 backdrop-blur-md rounded-2xl  shadow-lg ">
            {/* <h2 className="text-xl font-semibold text-gray-800">{heroData.videoCard.title}</h2>
            <p className="mt-2 text-gray-600">{heroData.videoCard.description}</p> */}
            <div className=" aspect-video  rounded-lg flex items-center justify-center">
              {/* <div className="w-16 h-16 bg-white/30 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.3 3.75a.75.75 0 011.2-.55l7.5 5.25a.75.75 0 010 1.1l-7.5 5.25a.75.75 0 01-1.2-.55V3.75z" />
                </svg>
              </div> */}
              <img src={heroImageUrl ?? ""} alt={typeof heroData?.title === "string" ? heroData.title : ""} className="w-full h-full object-cover rounded-md"/>
            </div>
          </div>
        </div>
      </div>
     
    </div>
  );
};


 const SubjectDetails = () => { 
  const {slug} = useParams<{ slug: string }>();
  if (!slug) {
    return null; // ✅ return null, not undefined
  }
  const [data,setData] =  useState<SubjectDTO | null>();;
  useEffect(()=>{
    xhr({
      url:`/s/getSubject/${slug}`,
      method:"GET"
    }).then(res=>setData(res.data))
      .catch(err=>err)
  },[])
  if(!data){
    return(
           <div className="flex items-center space-x-4 animate-pulse">
              <div className="h-12 w-12 rounded-full bg-gray-300" />
                  <div className="space-y-2">
                    <div className="h-4 w-[250px] rounded bg-gray-300" />
                    <div className="h-4 w-[200px] rounded bg-gray-300" />
              </div>
           </div>
    )

  }
  const s = data  
  
  if(s){
    return (
    <>
    <SEO 
      title={s.name}
      description={s.description}
      keywords={`${s.name}, online ${s.name.toLowerCase()} classes, ${s.name} tutoring, learn ${s.name.toLowerCase()}, worldotutor`}
      url={`/subject-details/${slug}`}
      type="website"
      og={{
        type: 'website',
        siteName: 'WorldoTutor',
        locale: 'en_US',
        image: {
          url: s.heroImageUrl || '/imgi_1_favicon.png',
          alt: `${s.name} - WorldoTutor`,
          width: 1200,
          height: 630
        }
      }}
      twitter={{
        card: 'summary_large_image',
        site: '@worldotutor',
        image: {
          url: s.heroImageUrl || '/imgi_1_favicon.png',
          alt: `${s.name} - WorldoTutor`
        }
      }}
    />
    <div >
      <HeroSection heroData={s.heroData} heroImageUrl={s.heroImageUrl} />
      <section className="container m-auto  py-10  ">
        <div className="mt-8 lg:mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <div>
            <h2 className="text-2xl lg:text-3xl font-semibold text-gray-900">Key Highlights of the Course</h2>
            <KeyHighlights items={s.highlights} />
            <h3 className="mt-8 text-lg font-semibold text-gray-900">Skills you will learn</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {s.skills.map((label) => (
                <span key={label} className="px-3 py-1 text-sm bg-blue-50 text-blue-700 rounded-md">{label}</span>
              ))}
            </div>
            <div>
              <div className="mt-10">
                <h2 className="text-3xl font-semibold text-gray-900">Curriculum</h2>
                <p className="mt-2 text-gray-600 max-w-3xl">
                  The curriculum consists of foundational and advanced competency tracks which enable learners to master
                  advanced data science skill sets effectively.
                </p>
                <div className="mt-4 text-white font-bold">
                  <Button
                    text="Download Curriculum"
                    onClick={() => {
                      const pdfUrl = s?.curriculumPdfUrl ?? "";
                      if (!pdfUrl) {
                        toast.error("No curriculum PDF URL found");
                        return;
                      }
                      window.open(pdfUrl, "_blank", "noopener,noreferrer");
                    }}
                    bgColor="bg-primary"
                  />
                </div>
              </div>
          
            </div>
             {/* Accordion */}
      <div className="py-16">
      <CurriculumAccordion data={s.curriculum} />
      </div>
 
{/* Course Duration & Options */}
<div className="py-10">
<CourseDurationOptions items={s.durationOptions} />
</div>

<div className="py-10">
    <CurriculumFlexibility items={s.flexibility} />
</div>
          </div>            
             
          <div className="lg:sticky lg:top-20 self-start">
    <EnquiryForm />
  </div>      
            
     
          
        </div>
           </section>
           <div className="bg-neutralBlack">

           <Footer/>
           </div>
    </div>
    </>
  );
  }else{
    return null;
  }
}

// Highlights list renderer

const KeyHighlights = ({ items }: { items: HighlightItem[] }) => {
  return (
    <ul className="mt-6 space-y-2">
      {items.map((item, idx) => (
        <li key={idx} className="flex items-start gap-3">
          <span className="text-primary text-xl">{item.icon}</span>
          <p className="text-gray-800">{item.text}</p>
        </li>
      ))}
    </ul>
  )
}

// Curriculum accordion

const CurriculumAccordion = ({ data }: { data: Trimester[] }) => {
  const [openTrimesters, setOpenTrimesters] = useState<Record<number, boolean>>({ 0: true })
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({ "0-0": true })

  const toggleTrimester = (idx: number) => {
    setOpenTrimesters((prev) => ({ ...prev, [idx]: !prev[idx] }))
  }

  const toggleItem = (tIdx: number, iIdx: number) => {
    const key = `${tIdx}-${iIdx}`
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }))
  } 

  return (
    <div className="mt-6 space-y-6">
      {data.map((trimester, tIdx) => {
        const isOpen = !!openTrimesters[tIdx]
        return (
          <div key={trimester.title} className="border rounded-xl shadow-sm bg-white">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h4 className="text-sm font-bold tracking-wide ">{trimester.title}</h4>
              <button
                type="button"
                aria-label={isOpen ? "Collapse" : "Expand"}
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-gray-100 "
                onClick={() => toggleTrimester(tIdx)}
              >
                {isOpen ? "−" : "+"}
              </button>
            </div>

            {isOpen && (
              <div className="p-4 space-y-4">
                {trimester.items.map((item, iIdx) => {
                  const key = `${tIdx}-${iIdx}`
                  const itemOpen = !!openItems[key]
                  return (
                    <div key={item.title} className="border rounded-lg">
                      <button
                        type="button"
                        className="w-full flex items-center justify-between text-left px-4 py-4"
                        onClick={() => toggleItem(tIdx, iIdx)}
                      >
                        <span className="font-semibold text-gray-900">{item.title}</span>
                        <span className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-gray-100 text-gray-700">
                          {itemOpen ? "−" : "+"}
                        </span>
                      </button>
                      {itemOpen && (
                        <div className="px-4 pb-4 text-gray-700">
                          {item.description}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// Course duration

const CourseDurationOptions = ({ items }: { items: DurationOption[] }) => {
  return (
    <div className="mt-10">
      <h2 className="text-2xl lg:text-3xl font-semibold text-gray-900">Course Duration & Options (45-Minute Classes)</h2>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((opt) => (
          <div key={opt.groupTitle} className="border rounded-xl bg-white shadow-sm p-5">
            <h3 className="text-lg font-semibold text-gray-900">{opt.groupTitle}</h3>
            <p className="mt-2 text-gray-700 font-medium">{opt.sessions}</p>
            <ul className="mt-3 space-y-2 text-gray-700">
              {opt.bullets.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  )
}

// Flexibility cards

const CurriculumFlexibility = ({ items }: { items: FlexibilityPoint[] }) => {
  return (
    <div className="mt-10">
      <h2 className="text-2xl lg:text-3xl font-semibold text-gray-900">Curriculum Flexibility</h2>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((p) => (
          <div key={p.title} className="border rounded-xl bg-white shadow-sm p-5">
            <h3 className="text-lg font-semibold text-gray-900">{p.title}</h3>
            <p className="mt-2 text-gray-700">{p.description}</p>
          </div>
        ))}
      </div>
    </div>
  )
}


export default function SubjectData(){
  return (<SubjectDetails/>)
}